import { useEffect, useRef, type ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { WORKBENCH_SEARCH_PILL_CLASS } from "./workbenchSurface";

export interface WorkbenchSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Inline content rendered inside the pill, right of the input (e.g. chips). */
  accessory?: ReactNode;
  /** Focus the input when mounted (toolbar hover overlay). */
  focusOnMount?: boolean;
  /** When false, omit the left magnifying-glass (e.g. hover overlay with a toolbar trigger). */
  showLeadingIcon?: boolean;
  /** Borderless native input inside an optional outer pill (default) or a plain flex row. */
  embedded?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  "data-testid"?: string;
}

/**
 * Generic search pill for list workbenches: magnifying-glass affix, borderless
 * native input, and an optional inline accessory slot (active-filter chips, counts…).
 *
 * Use `embedded` when a parent already owns the outer border (toolbar hover overlay).
 * Default mode draws the pill chrome here — do not nest FormInput inside it.
 */
export function WorkbenchSearchInput({
  value,
  onChange,
  placeholder = "Search…",
  accessory,
  focusOnMount = false,
  showLeadingIcon = true,
  embedded = false,
  onFocus,
  onBlur,
  className,
  "data-testid": testId = "workbench-search",
}: WorkbenchSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focusOnMount) return;
    inputRef.current?.focus();
  }, [focusOnMount]);

  const inputClassName =
    "min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-0 dark:text-slate-200 dark:placeholder:text-slate-500";

  const input = (
    <input
      ref={inputRef}
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      className={inputClassName}
      data-testid={testId}
    />
  );

  if (embedded) {
    return (
      <div className={clsx("flex min-w-0 flex-1 items-center gap-2 px-3", className)}>
        {showLeadingIcon ? (
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none shrink-0 text-slate-500"
          />
        ) : null}
        {input}
        {accessory ? (
          <div className="flex max-w-[50%] shrink-0 items-center gap-1 overflow-x-auto">
            {accessory}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "group relative flex items-center gap-1 rounded-xl pr-1",
        WORKBENCH_SEARCH_PILL_CLASS,
        showLeadingIcon ? "pl-9" : "pl-3",
        className,
      )}
    >
      {showLeadingIcon ? (
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
        />
      ) : null}
      {input}
      {accessory ? (
        <div className="flex max-w-[50%] shrink-0 items-center gap-1 overflow-x-auto">
          {accessory}
        </div>
      ) : null}
    </div>
  );
}
